import React, { useEffect, useState, Suspense, lazy } from "react";
import ReactGA from "react-ga4";
import { Box, Container, ThemeProvider } from "@mui/material";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";

import { AuthProvider } from "./context/auth.jsx";
import { AuthModalProvider } from "./context/authModal.jsx";
import ToastProvider from "./context/toast.jsx";

import theme from "./Theme/Default/theme.js";
import axios from "./libraries/axios.js";
import ArticleContext from "./libraries/context2.js";

import ProtectedRoute from "./components/ProtectedRoute.jsx";
import Loader from "./components/Loader.jsx";

import "./App.css";
import { CategoriesQuery } from "./server/query/index.js";

// Lazy Loaded Components
const Profile = lazy(() => import("./Pages/UserDashboard/Profile.jsx"));
const Disclaimer = lazy(() => import("./Popup/Disclaimer.jsx"));
const Home = lazy(() => import("./components/Home.jsx"));
const Header = lazy(() => import("./components/Header.jsx"));
const NavBar = lazy(() => import("./components/NavBar.jsx"));
const Footer = lazy(() => import("./components/Footer/index.jsx"));
const HomeCategory = lazy(() => import("./components/HomeCategory.jsx"));
const AtclContainer = lazy(() => import("./components/AtclContainer.jsx"));
const CategoryNavbar = lazy(() => import("./components/CategoryNavbar.jsx"));

ReactGA.initialize("G-9B95PTS6EF");

function App() {
	const [currentArticle, setCurrentArticle] = useState();
	const [navLinks, setNavLinks] = useState([]);
	const [swtDic, setSwtDic] = useState(false);

	useEffect(() => {
		fetchCategories();
	}, []);

	const fetchCategories = async () => {
		try {
			const { data } = await axios({
				method: "POST",
				data: { query: CategoriesQuery },
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				},
			});

			if (data?.data?.categories) {
				const links = data.data.categories
					.filter((item) => item.category !== "testcategory" && item.category !== "testcategory2")
					.map((item) => ({
						id: item.id,
						link: `/${item.category.toLowerCase()}`,
						title: item.category,
					}));
				setNavLinks(links);
			} else if (data?.errors?.[0]?.message) {
				alert(data.errors[0].message);
			}
		} catch (error) {
			console.error("Category Fetching Error:", error);
		}
	};

	return (
		<ToastProvider>
			<AuthProvider>
				<ArticleContext.Provider value={{ currentArticle, setCurrentArticle }}>
					<ThemeProvider theme={theme}>
						<AuthModalProvider>
							<Box
								className="app"
								minHeight="100vh"
								display="flex"
								flexDirection="column"
							>
								<Router>
									<Suspense fallback={<Loader visibility={true} />}>
										<Switch>
											<ProtectedRoute
												path="/dashboard/profile"
												component={Profile}
											/>

											<Route path="/article">
												<Header />
												<Box bgcolor={(theme) => theme.palette.grey["100"]}>
													<Container
														disableGutters
														maxWidth="xl"
														sx={{ bgcolor: "white" }}
													>
														<CategoryNavbar />
														<AtclContainer />
													</Container>
												</Box>
											</Route>

											{navLinks.map((item) => (
												<Route path={item.link} key={item.id}>
													<Header />
													<Box
														bgcolor={(theme) => theme.palette.grey["100"]}
														flex="1 1 0"
														display="flex"
														flexDirection="column"
													>
														<Container
															disableGutters
															maxWidth="xl"
															sx={(theme) => ({
																bgcolor: theme.palette.grey["50"],
																flex: "1 1 0",
															})}
														>
															<CategoryNavbar />
															<HomeCategory />
														</Container>
													</Box>
													<Footer setSwtDic={setSwtDic} />
												</Route>
											))}

											<Route path="/">
												<Header />
												<Box bgcolor={(theme) => theme.palette.grey["100"]}>
													<Container
														maxWidth="xl"
														disableGutters
														sx={{ bgcolor: "white" }}
													>
														<NavBar />
														<CategoryNavbar />
														<Home />
													</Container>
												</Box>
												<Footer setSwtDic={setSwtDic} />
											</Route>
										</Switch>
									</Suspense>
								</Router>
							</Box>

							<Suspense fallback={null}>
								{swtDic && <Disclaimer setSwtDic={setSwtDic} swtDic={swtDic} />}
							</Suspense>
						</AuthModalProvider>
					</ThemeProvider>
				</ArticleContext.Provider>
			</AuthProvider>
		</ToastProvider>
	);
}

export default App;